/**
 * Product bundles (Jewelique)
 *
 * <jewelique-bundles> renders set cards, a "Show more" toggle, an offer banner
 * and a bottom-sheet <dialog> where the shopper picks options for the three
 * products (step by step, or all at once) before the set is added to the cart
 * as three individual line items tagged with hidden `_bundle_*` properties.
 *
 * All copy, colours and behaviour switches come from the section settings
 * (see sections/product-bundles.liquid).
 */
(() => {
  if (customElements.get('jewelique-bundles')) return;

  const make = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const setText = (el, text) => {
    if (el && el.textContent !== text) el.textContent = text;
  };
  const normalise = (value) =>
    String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, ' ');
  const SIZE_PATTERN = /size|مقاس|قياس/i;
  const COLOUR_PATTERN = /colou?r|لون/i;
  const DESIGN_PATTERN = /design|تصميم/i;
  const NUMERIC_PATTERN = /^[\d\s()\-–.,]+$/;
  // Shopify CDN URLs carry the requested rendition as a `width` query parameter.
  const thumbSrc = (src) => (typeof src === 'string' ? src.replace(/([?&])width=\d+/, '$1width=168') : src);

  const BUILTIN_SWATCHES = {
    gold: 'linear-gradient(135deg, #f6e27a, #c9a227 60%, #a67c00)',
    'rose gold': 'linear-gradient(135deg, #f4c2c2, #b76e79)',
    silver: 'linear-gradient(135deg, #f7f7f7, #c4c4c4 60%, #8f8f8f)',
    white: '#f8f8f8',
    black: '#111111',
    red: '#b3261e',
    'dark red': '#7a1f2b',
    burgundy: '#7a1f2b',
    green: '#2e7d4f',
    'dark green': '#1f5b3a',
    pink: '#e8a2b8',
    blue: '#2f5fa8',
    navy: '#1f2f5a',
    purple: '#6b3fa0',
    brown: '#6b4a2b',
    beige: '#e6d5b8',
    grey: '#9a9a9a',
    gray: '#9a9a9a',
    // Arabic
    'ذهبي': 'linear-gradient(135deg, #f6e27a, #c9a227 60%, #a67c00)',
    'ذهبي وردي': 'linear-gradient(135deg, #f4c2c2, #b76e79)',
    'فضي': 'linear-gradient(135deg, #f7f7f7, #c4c4c4 60%, #8f8f8f)',
    'أبيض': '#f8f8f8',
    'ابيض': '#f8f8f8',
    'أسود': '#111111',
    'اسود': '#111111',
    'أحمر': '#b3261e',
    'احمر': '#b3261e',
    'أحمر غامق': '#7a1f2b',
    'احمر غامق': '#7a1f2b',
    'أخضر': '#2e7d4f',
    'اخضر': '#2e7d4f',
    'وردي': '#e8a2b8',
    'زهري': '#e8a2b8',
    'أزرق': '#2f5fa8',
    'ازرق': '#2f5fa8',
  };

  /**
   * Parses "Name, alias: value" lines (also "Name = value") into a map keyed by
   * every normalised alias.
   */
  function parseAliasMap(text) {
    const map = {};
    String(text || '')
      .split(/\r?\n/)
      .forEach((line) => {
        const match = line.match(/^\s*([^:=]+?)\s*[:=]\s*(.+?)\s*$/);
        if (!match) return;
        match[1]
          .split(',')
          .map(normalise)
          .filter(Boolean)
          .forEach((alias) => {
            map[alias] = match[2];
          });
      });
    return map;
  }

  class JeweliqueBundles extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.strings = JSON.parse(this.querySelector('[data-jq-strings]').textContent);
      this.dialog = this.querySelector('dialog');
      this.list = this.querySelector('[data-jq-options]');
      this.addButton = this.querySelector('[data-jq-add]');
      this.nextButton = this.querySelector('[data-jq-next]');
      this.backButton = this.querySelector('[data-jq-back]');
      this.status = this.querySelector('[data-jq-status]');
      this.error = this.querySelector('[data-jq-error]');
      this.feedback = this.querySelector('[data-jq-feedback]');
      this.showMoreButton = this.querySelector('[data-jq-show-more]');
      // routes.root_url is "/" for the default language but "/en" (no slash) for others.
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/');
      this.cartUrl = this.dataset.cartUrl || `${this.root}cart`;
      this.currency = this.dataset.currency || 'AED';
      this.moneyTemplate = this.dataset.moneyFormat && this.dataset.moneyFormat.includes('[amount]') ? this.dataset.moneyFormat : null;
      this.layout = this.dataset.layout === 'single' ? 'single' : 'steps';
      this.offerAmount = parseInt(this.dataset.offerAmount, 10) || 0;
      this.showSwatches = this.dataset.showSwatches !== 'false';
      this.swatchMap = parseAliasMap(this.strings.colour_swatches);
      this.valueLabels = parseAliasMap(this.strings.option_value_labels);
      this.number = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
      this.intlMoney = new Intl.NumberFormat(this.lang === 'ar' ? 'ar-AE' : 'en-AE', {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
      this.cache = new Map();
      this.memory = new Map();
      this.supportsDialog = typeof this.dialog.showModal === 'function';
      if (this.layout === 'steps') this.dialog.classList.add('jq-sheet--steps');
      const offer = this.querySelector('[data-jq-offer]');
      if (offer) this.offerDefaultText = offer.textContent;

      this.querySelectorAll('[data-jq-card]').forEach((card) => {
        const payload = card.querySelector('[data-jq-bundle]');
        if (!payload) return;
        let data;
        try {
          data = JSON.parse(payload.textContent);
        } catch (error) {
          console.error('[jewelique-bundles] invalid bundle payload', error);
          return;
        }
        data.card = card;
        this.cache.set(data.key, data);
        card.querySelector('[data-jq-open]')?.addEventListener('click', (event) => this.open(data, event.currentTarget));
      });

      this.querySelector('[data-jq-close]')?.addEventListener('click', () => {
        if (!this.busy) this.closeDialog();
      });
      this.dialog.addEventListener('cancel', (event) => {
        if (this.busy) event.preventDefault();
        else this.remember();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog || this.busy) return;
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) {
          this.closeDialog();
        }
      });
      this.dialog.addEventListener('close', () => this.release());
      this.addButton.addEventListener('click', () => this.add());
      this.nextButton?.addEventListener('click', () => this.onNext());
      this.backButton?.addEventListener('click', () => {
        if (!this.busy) this.goToStep(this.stepIndex - 1);
      });
      this.showMoreButton?.addEventListener('click', () => this.toggleMore());

      document.addEventListener('jewelique:cart', (event) => this.updateOffer(event.detail));
      document.addEventListener('shopify:block:select', (event) => {
        const card = event.detail && this.querySelector(`[data-key="${event.detail.blockId}"]`);
        if (card && card.hidden) this.toggleMore(true);
      });

      // Let the cart's "Choose your second set" link find this section.
      if (!document.getElementById('jewelique-bundles')) {
        this.anchor = make('span');
        this.anchor.id = 'jewelique-bundles';
        this.prepend(this.anchor);
        if (window.location.hash === '#jewelique-bundles') {
          requestAnimationFrame(() => this.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        }
      }

      this.refreshOffer();
    }

    disconnectedCallback() {
      if (this.dialog?.open) {
        this.closeDialog();
        this.release();
      }
    }

    /* ---------------------------------------------------------------- */
    /* Helpers                                                           */
    /* ---------------------------------------------------------------- */

    t(key) {
      const value = this.strings[key];
      return value == null ? '' : String(value);
    }

    /** Formats cents using the storefront money format (same as Liquid's money filter). */
    format(cents) {
      if (this.moneyTemplate) return this.moneyTemplate.replace('[amount]', this.number.format(cents / 100));
      return this.intlMoney.format(cents / 100);
    }

    /** Display label for an option value (merchant mapping, else the raw value). */
    label(value) {
      return this.valueLabels[normalise(value)] || value;
    }

    /** Colour used for an option value: merchant mapping → Shopify swatch → built-in names. */
    swatchFor(item, value) {
      if (!this.showSwatches) return null;
      const key = normalise(value);
      const own = item.product.swatches && (item.product.swatches[value] || item.product.swatches[key]);
      return this.swatchMap[key] || own || BUILTIN_SWATCHES[key] || null;
    }

    optionTitle(item, name, optionIndex) {
      if (item.sizeIndices.includes(optionIndex) && this.t('size_option_label')) return this.t('size_option_label');
      if (optionIndex === item.colourIndex && this.t('colour_option_label')) return this.t('colour_option_label');
      if (DESIGN_PATTERN.test(name) && this.t('design_option_label')) return this.t('design_option_label');
      return name;
    }

    /* ---------------------------------------------------------------- */
    /* Show more                                                         */
    /* ---------------------------------------------------------------- */

    toggleMore(forceOpen) {
      const cards = [...this.querySelectorAll('[data-jq-more]')];
      if (!cards.length) return;
      const expanded = forceOpen === true ? true : this.showMoreButton?.getAttribute('aria-expanded') !== 'true';
      cards.forEach((card) => {
        card.hidden = !expanded;
      });
      if (!this.showMoreButton) return;
      this.showMoreButton.setAttribute('aria-expanded', String(expanded));
      if (expanded) {
        if (this.t('show_less')) {
          this.showMoreButton.textContent = this.t('show_less');
        } else {
          this.showMoreButton.hidden = true;
        }
        if (forceOpen !== true) {
          const heading = cards[0].querySelector('h3') || cards[0];
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }
      } else {
        this.showMoreButton.textContent = this.t('show_more').replace('[count]', String(cards.length));
        this.showMoreButton.hidden = false;
        const lastVisible = [...this.querySelectorAll('[data-jq-card]:not([hidden])')].pop();
        lastVisible?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    /* ---------------------------------------------------------------- */
    /* Offer banner                                                      */
    /* ---------------------------------------------------------------- */

    async refreshOffer() {
      try {
        const response = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (response.ok) this.updateOffer(await response.json());
      } catch (error) {
        /* keep the static banner */
      }
    }

    cartHoldsOneSet(cart) {
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) return false;
      const bundleItems = cart.items.filter((item) => item.properties && item.properties._bundle_id);
      if (bundleItems.length !== cart.items.length) return false;
      const ids = new Set(bundleItems.map((item) => item.properties._bundle_id));
      const size = parseInt(bundleItems[0].properties._bundle_size, 10) || 3;
      return ids.size === 1 && bundleItems.length === size && bundleItems.every((item) => item.quantity === 1);
    }

    /**
     * A set already in the cart (or any three pieces) means the next set is charged
     * at the two-set offer, so the Add button stops quoting the single-set price.
     */
    nextSetIsExtra() {
      const cart = this.cart;
      if (!cart || !Array.isArray(cart.items)) return false;
      return this.cartHoldsOneSet(cart) || (parseInt(cart.item_count, 10) || 0) >= 3;
    }

    /** Remembers the cart, then only rewrites the banner when the upsell applies; otherwise the Liquid text stays untouched. */
    updateOffer(cart) {
      if (!cart) return;
      this.cart = cart;
      if (this.dialog?.open && !this.busy && Array.isArray(this.items)) this.update();
      const offer = this.querySelector('[data-jq-offer]');
      if (!offer || !this.offerAmount) return;
      const remaining = this.offerAmount - cart.total_price;
      const upsell = cart.currency === this.currency && this.cartHoldsOneSet(cart) && remaining > 0 && this.t('upsell');
      const text = upsell ? this.t('upsell').replace('[amount]', this.format(remaining)) : this.offerDefaultText;
      if (offer.textContent !== text) offer.textContent = text;
    }

    /* ---------------------------------------------------------------- */
    /* Sheet                                                             */
    /* ---------------------------------------------------------------- */

    open(data, trigger) {
      if (this.busy) return;
      if (!this.supportsDialog) {
        // Very old browsers without <dialog>: send the shopper to the first product instead.
        window.location.assign(data.items[0]?.url || this.cartUrl);
        return;
      }
      this.active = data;
      this.priceCents = parseInt(data.price, 10) || 0;
      this.priceText = data.priceFormatted || this.format(data.price);
      this.addedWithoutConfirmation = false;
      this.trigger = trigger;
      this.querySelector('[data-jq-dialog-title]').textContent = data.title;
      this.querySelector('[data-jq-dialog-price]').textContent = this.priceText;
      this.error.hidden = true;
      this.list.replaceChildren();

      const memory = this.memory.get(data.key);

      this.items = data.items.map((product, index) => {
        const initial =
          product.variants.find((v) => v.id === product.defaultVariant && v.available) ||
          product.variants.find((v) => v.available) ||
          product.variants[0];
        const values = product.options.map((name, i) => [...new Set(product.variants.map((v) => v.options[i]))]);
        const sizeIndices = product.options
          .map((name, i) => (SIZE_PATTERN.test(name) && values[i].length > 1 ? i : -1))
          .filter((i) => i >= 0);
        const remembered = memory?.selected?.[index];
        return {
          product,
          index,
          values,
          sizeIndices,
          selected: remembered ? [...remembered] : product.options.map((name, i) => (sizeIndices.includes(i) ? null : initial?.options[i])),
          fields: [],
          colourIndex: product.options.findIndex((n) => COLOUR_PATTERN.test(n)),
        };
      });

      this.sharedColours = [];
      if (this.items.every((item) => item.colourIndex >= 0)) {
        this.sharedColours = this.items[0].values[this.items[0].colourIndex].filter((colour) =>
          this.items.every((item) => item.product.variants.some((v) => v.available && v.options[item.colourIndex] === colour))
        );
      }
      this.shared = this.sharedColours.length > 0 && (memory ? memory.shared : true);
      this.sharedCheckbox = null;
      this.sharedField = null;
      this.sharedInputs = null;
      if (this.sharedColours.length) {
        const preferred = normalise(this.t('default_colour'));
        this.sharedValue =
          (memory && this.sharedColours.includes(memory.sharedValue) && memory.sharedValue) ||
          this.sharedColours.find((c) => normalise(c) === preferred || normalise(this.label(c)) === preferred) ||
          this.sharedColours[0];
        if (this.shared) {
          this.items.forEach((item) => {
            item.selected[item.colourIndex] = this.sharedValue;
          });
        }
      }

      if (this.layout === 'steps') this.buildSteps(memory?.step || 0);
      else this.buildSingle();

      this.update();
      this.scrollY = window.scrollY;
      document.documentElement.style.setProperty('--jq-scroll-offset', `${-this.scrollY}px`);
      document.documentElement.classList.add('jq-sheet-open');
      this.dialog.showModal();
      // Focus the title rather than a control so nothing lights up on open; Tab reaches the close button next.
      const title = this.querySelector('[data-jq-dialog-title]');
      if (!title.hasAttribute('tabindex')) title.tabIndex = -1;
      title.focus({ preventScroll: true });
    }

    remember() {
      if (!this.active || !this.items) return;
      this.memory.set(this.active.key, {
        selected: this.items.map((item) => [...item.selected]),
        shared: this.shared,
        sharedValue: this.sharedValue,
        step: this.stepIndex || 0,
      });
    }

    closeDialog() {
      this.remember();
      this.dialog.close();
    }

    release() {
      if (this.busy) {
        this.pendingRelease = true;
        return;
      }
      this.pendingRelease = false;
      document.documentElement.classList.remove('jq-sheet-open');
      document.documentElement.style.removeProperty('--jq-scroll-offset');
      if (Number.isFinite(this.scrollY)) window.scrollTo({ top: this.scrollY, behavior: 'instant' });
      this.trigger?.focus({ preventScroll: true });
    }

    buildSingle() {
      this.steps = null;
      if (this.sharedColours.length) this.list.append(...this.buildSharedColour());
      this.items.forEach((item) => this.list.append(this.buildItem(item)));
    }

    buildSteps(startAt) {
      this.stepIndex = 0;
      const header = make('div', 'jq-steps');
      this.stepText = make('span', 'jq-steps__text');
      this.stepDots = make('span', 'jq-steps__dots');
      this.items.forEach(() => this.stepDots.append(make('span', 'jq-steps__dot')));
      header.append(this.stepText, this.stepDots);
      this.list.append(header);

      this.steps = this.items.map((item, index) => {
        const step = make('section', 'jq-step');
        step.hidden = true;
        step.setAttribute('aria-label', item.product.title);

        const hero = make('div', 'jq-step__hero');
        const img = make('img');
        img.alt = item.product.title;
        img.width = 400;
        img.height = 400;
        img.decoding = 'async';
        hero.append(img);
        item.hero = img;
        step.append(hero);

        const gallery = [...new Set([item.product.image, ...(item.product.images || [])].filter(Boolean))];
        if (gallery.length > 1) {
          const thumbs = make('div', 'jq-step__thumbs');
          item.thumbs = gallery.slice(0, 8).map((src, thumbIndex, all) => {
            const button = make('button', 'jq-step__thumb');
            button.type = 'button';
            button.setAttribute('aria-label', `${item.product.title} ${thumbIndex + 1}/${all.length}`);
            const thumb = make('img');
            thumb.src = thumbSrc(src);
            thumb.alt = '';
            thumb.loading = 'lazy';
            thumb.width = 56;
            thumb.height = 56;
            button.append(thumb);
            const warm = () => { new Image().src = src; };
            button.addEventListener('pointerenter', warm, { once: true });
            button.addEventListener('focusin', warm, { once: true });
            button.addEventListener('click', () => this.setHero(item, src));
            thumbs.append(button);
            return { button, src };
          });
          step.append(thumbs);
        }

        if (index === 0 && this.sharedColours.length) step.append(...this.buildSharedColour());
        step.append(this.buildItem(item, index));
        this.list.append(step);
        return step;
      });
      this.goToStep(Math.min(startAt || 0, this.steps.length - 1), true);
    }

    setHero(item, src) {
      if (!item.hero) return;
      if (item.hero.getAttribute('src') !== src) item.hero.src = src;
      item.thumbs?.forEach(({ button, src: thumbSrc }) => button.classList.toggle('jq-step__thumb--active', thumbSrc === src));
    }

    stepLabel(index) {
      return this.t('step')
        .replace('[current]', String(index + 1))
        .replace('[total]', String(this.steps.length))
        .replace('[product]', this.items[index].product.title);
    }

    goToStep(index, silent) {
      if (!this.steps) return;
      const clamped = Math.max(0, Math.min(this.steps.length - 1, index));
      this.stepIndex = clamped;
      this.steps.forEach((step, i) => {
        step.hidden = i !== clamped;
      });
      [...this.stepDots.children].forEach((dot, i) => {
        dot.classList.toggle('jq-steps__dot--active', i === clamped);
        dot.classList.toggle('jq-steps__dot--done', i < clamped);
      });
      this.stepText.textContent = this.stepLabel(clamped);
      // Warm up the next step's image so the switch does not flash.
      const nextItem = this.items[clamped + 1];
      if (nextItem) {
        const src = this.matching(nextItem).find((v) => v.available)?.image || nextItem.product.image;
        if (src) new Image().src = src;
      }
      if (!silent) {
        this.list.scrollTo({ top: 0, behavior: 'instant' });
        this.update();
        const target = this.steps[clamped].querySelector('h4') || this.steps[clamped];
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    }

    onNext() {
      if (!this.steps || this.busy) return;
      const current = this.items[this.stepIndex];
      if (current.incomplete || current.unavailable) {
        // Guide the shopper to what is missing instead of doing nothing.
        const step = this.steps[this.stepIndex];
        const target =
          step.querySelector('.jq-required:not([hidden])')?.closest('fieldset') ||
          step.querySelector('.jq-step__note:not([hidden])') ||
          step.querySelector('fieldset');
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target?.classList.add('jq-option--attention');
        setTimeout(() => target?.classList.remove('jq-option--attention'), 1200);
        return;
      }
      this.goToStep(this.stepIndex + 1);
    }

    chipGroup(title, values, selected, name, onChange, swatchItem) {
      const field = make('fieldset', 'jq-option');
      field.append(make('legend', '', title));
      const options = make('div', 'jq-options');
      const inputs = [];
      values.forEach((value, index) => {
        const label = make('label', 'jq-chip');
        const input = make('input');
        input.type = 'radio';
        input.name = name;
        input.value = value;
        input.id = `${name}-${index}`;
        input.checked = value === selected;
        input.addEventListener('change', () => onChange(value));
        const text = make('span');
        const swatch = swatchItem ? this.swatchFor(swatchItem, value) : null;
        if (swatch) {
          const dot = make('span', 'jq-swatch');
          dot.style.setProperty('--jq-swatch', swatch);
          dot.setAttribute('aria-hidden', 'true');
          text.append(dot);
          label.classList.add('jq-chip--swatch');
        }
        const caption = make('span', 'jq-chip__label', this.label(value));
        if (NUMERIC_PATTERN.test(value)) caption.dir = 'ltr';
        text.append(caption);
        label.append(input, text);
        options.append(label);
        inputs.push(input);
      });
      field.append(options);
      return { field, inputs };
    }

    buildSharedColour() {
      const { field, inputs } = this.chipGroup(
        this.t('shared_colour'),
        this.sharedColours,
        this.sharedValue,
        `${this.dataset.sectionId}-shared`,
        (value) => {
          this.sharedValue = value;
          this.items.forEach((item) => {
            item.selected[item.colourIndex] = value;
          });
          this.update();
        },
        this.items[0]
      );
      this.sharedField = field;
      this.sharedInputs = inputs;
      this.sharedNote = make('p', 'jq-step__note');
      this.sharedNote.hidden = true;
      field.append(this.sharedNote);
      const row = make('label', 'jq-custom-colours');
      const check = make('input');
      check.type = 'checkbox';
      check.checked = !this.shared;
      field.hidden = !this.shared;
      check.addEventListener('change', () => this.setSharedColours(!check.checked));
      this.sharedCheckbox = check;
      row.append(check, make('span', '', this.t('individual_colours')));
      return [field, row];
    }

    setSharedColours(shared) {
      this.shared = shared;
      if (this.sharedCheckbox) this.sharedCheckbox.checked = !shared;
      if (this.sharedField) this.sharedField.hidden = !shared;
      if (shared) {
        this.items.forEach((item) => {
          item.selected[item.colourIndex] = this.sharedValue;
        });
      }
      this.update();
    }

    buildItem(item, stepIndex) {
      const row = make('section', 'jq-choice-item');
      row.setAttribute('aria-label', item.product.title);
      const top = make('div', 'jq-choice-item__heading');
      const img = make('img');
      img.width = 72;
      img.height = 72;
      img.alt = item.product.title;
      const heading = make('div');
      heading.append(make('h4', '', item.product.title));
      item.selectionText = make('p', 'jq-choice-summary');
      heading.append(item.selectionText);
      top.append(img, heading);
      row.append(top);
      item.image = img;

      // Steps 2+: show the shared colour with a shortcut to change it for this piece.
      if (stepIndex > 0 && item.colourIndex >= 0 && this.sharedColours.length && this.t('change_colour')) {
        item.sharedLine = make('p', 'jq-fixed-option jq-shared-line');
        const button = make('button', 'jq-step__note-button', this.t('change_colour'));
        button.type = 'button';
        button.addEventListener('click', () => this.revealColour(item));
        item.sharedLineText = make('span');
        item.sharedLine.append(item.sharedLineText, ' ', button);
        row.append(item.sharedLine);
      }

      item.product.options.forEach((name, optionIndex) => {
        const values = item.values[optionIndex];
        if (name === 'Title' && values.length === 1 && values[0] === 'Default Title') return;
        const isSize = item.sizeIndices.includes(optionIndex);
        const isColour = optionIndex === item.colourIndex;
        const title = this.optionTitle(item, name, optionIndex);
        if (values.length === 1) {
          const staticText = make('p', 'jq-fixed-option', this.label(values[0]));
          row.append(staticText);
          item.fields.push({ field: staticText, optionIndex, inputs: [] });
          return;
        }
        const { field, inputs } = this.chipGroup(
          title,
          values,
          item.selected[optionIndex],
          `${this.dataset.sectionId}-${item.index}-${optionIndex}`,
          (value) => {
            item.selected[optionIndex] = value;
            this.update();
          },
          isColour ? item : null
        );
        if (isSize) {
          field.classList.add('jq-option--size');
          field.querySelector('legend').append(make('span', 'jq-required', this.t('required')));
          inputs.forEach((input) => {
            input.required = true;
          });
        }
        row.append(field);
        item.fields.push({ field, inputs, optionIndex, isSize, isColour });
      });

      if (item.sizeIndices.length) {
        const guide = this.buildGuide(item);
        if (guide) row.append(guide);
      }
      return row;
    }

    revealColour(item) {
      this.setSharedColours(false);
      const colourField = item.fields.find((f) => f.optionIndex === item.colourIndex)?.field;
      colourField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      colourField?.querySelector('input:not(:disabled)')?.focus({ preventScroll: true });
    }

    /**
     * Size guide textarea grammar:
     *   plain line              → hint sentence
     *   "Size | Wrist (cm)"     → header row when the first cell is not a size value
     *   "16 | 15–16 cm"         → table row
     * Blank textarea: sizes written like "6 (52)" build the table automatically.
     */
    parseGuide(item) {
      const kind = item.product.kind === 'ring' ? 'ring' : 'wrist';
      // The set's own guide wins; otherwise the section's default table for this kind of piece.
      const raw = String(item.product.sizeGuide || this.t(kind === 'ring' ? 'ring_table' : 'wrist_table') || '').trim();
      const typedHint = Boolean(item.product.sizeGuide);
      const sizeSet = new Set(item.values[item.sizeIndices[0]].map((v) => normalise(v.split('(')[0])));
      let hint = '';
      let header = null;
      const rows = [];
      if (raw) {
        raw
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean)
          .forEach((line) => {
            const parts = line.split('|').map((part) => part.trim());
            if (parts.length >= 2) {
              if (!header && !rows.length && !sizeSet.has(normalise(parts[0]))) header = parts;
              else rows.push(parts);
            } else if (!hint && !rows.length) {
              hint = line;
            }
          });
      }
      if (!hint && !typedHint) hint = this.t(kind === 'ring' ? 'measure_ring' : 'measure_wrist');
      if (!rows.length) {
        // No table typed in: sizes written like "6 (52)" build one automatically.
        const sizeValues = item.values[item.sizeIndices[0]];
        if (sizeValues.every((v) => /\(.+\)/.test(v))) {
          rows.push(...sizeValues.map((v) => [v.split('(')[0].trim(), v.match(/\((.+)\)/)[1]]));
        }
      }
      const measurement = this.t(kind === 'ring' ? 'ring_col' : 'wrist_col') || this.t('measurement_col');
      return { hint, header: header || [this.t('size_col'), measurement], rows };
    }

    buildGuide(item) {
      const { hint, header, rows } = this.parseGuide(item);
      if (!this.t('size_guide') || (!hint && !rows.length)) return null;
      const details = make('details', 'jq-size-guide');
      details.append(make('summary', '', this.t('size_guide')));
      if (hint) details.append(make('p', '', hint));
      if (rows.length) {
        const table = make('table');
        const head = make('thead');
        const tr = make('tr');
        header.forEach((value) => {
          const th = make('th', '', value);
          th.scope = 'col';
          tr.append(th);
        });
        head.append(tr);
        table.append(head);
        const body = make('tbody');
        rows.forEach((values) => {
          const r = make('tr');
          header.forEach((_, i) => {
            const value = values[i] ?? '';
            const cell = make('td');
            cell.append(make('bdi', '', value));
            r.append(cell);
          });
          body.append(r);
        });
        table.append(body);
        details.append(table);
      }
      return details;
    }

    matching(item, selected = item.selected) {
      return item.product.variants.filter((v) => v.options.every((value, i) => selected[i] == null || selected[i] === value));
    }

    availableFor(item, optionIndex, value) {
      const candidate = [...item.selected];
      candidate[optionIndex] = value;
      return this.matching(item, candidate).some((v) => v.available);
    }

    /**
     * Drops selections that became unavailable (e.g. after a shared colour
     * change): sizes go back to "not chosen" so the required marker reappears,
     * other options jump to the first available value.
     */
    reconcileSelections(item) {
      if (this.shared && item.colourIndex >= 0) {
        // The shared colour is authoritative: when it no longer fits the chosen
        // size, clear the size so the required marker reappears on the chips.
        const colour = item.selected[item.colourIndex];
        if (colour != null && !this.availableFor(item, item.colourIndex, colour)) {
          item.sizeIndices.forEach((i) => {
            item.selected[i] = null;
          });
        }
      }
      item.fields.forEach(({ inputs, optionIndex, isSize }) => {
        if (!inputs.length) return;
        if (this.shared && optionIndex === item.colourIndex) return;
        const current = item.selected[optionIndex];
        if (current == null || this.availableFor(item, optionIndex, current)) return;
        if (isSize) {
          item.selected[optionIndex] = null;
        } else {
          const next = inputs.map((input) => input.value).find((value) => this.availableFor(item, optionIndex, value));
          item.selected[optionIndex] = next ?? null;
        }
      });
    }

    noteFor(field) {
      let note = field.querySelector('.jq-step__note');
      if (!note) {
        note = make('p', 'jq-step__note');
        note.hidden = true;
        field.append(note);
      }
      return note;
    }

    update() {
      let missing = false;
      let unavailable = false;
      this.items.forEach((item) => {
        this.reconcileSelections(item);
        item.fields.forEach(({ field, inputs, optionIndex, isSize }) => {
          field.hidden = this.shared && optionIndex === item.colourIndex;
          let anyAvailable = inputs.length === 0;
          inputs.forEach((input) => {
            const available = this.availableFor(item, optionIndex, input.value);
            anyAvailable ||= available;
            input.disabled = !available;
            input.checked = item.selected[optionIndex] === input.value;
            input.parentElement.classList.toggle('jq-chip--sold-out', !available);
            input.setAttribute('aria-label', `${this.label(input.value)}${available ? '' : ` — ${this.t('sold_out')}`}`);
          });
          const required = field.querySelector('.jq-required');
          if (required) required.hidden = item.selected[optionIndex] != null;

          // Dead end: every size is sold out for the colour chosen for all pieces.
          if (isSize && inputs.length) {
            const note = this.noteFor(field);
            const colour = item.colourIndex >= 0 ? item.selected[item.colourIndex] : null;
            const showNote = !anyAvailable && this.shared && colour != null && this.t('colour_unavailable');
            note.hidden = !showNote;
            if (showNote) {
              note.replaceChildren(make('span', '', this.t('colour_unavailable').replace(/\[colou?r\]/g, this.label(colour))));
              if (this.t('change_colour')) {
                const button = make('button', 'jq-step__note-button', this.t('change_colour'));
                button.type = 'button';
                button.addEventListener('click', () => this.revealColour(item));
                note.append(' ', button);
              }
            }
          }
        });
        const matches = this.matching(item);
        const candidate = matches.find((v) => v.available) || matches[0];
        const incomplete = item.selected.some((v, i) => v == null && item.values[i].length > 1);
        item.incomplete = incomplete;
        item.unavailable = !candidate?.available;
        missing ||= incomplete;
        item.variant = incomplete ? null : candidate;
        unavailable ||= item.unavailable;
        const src = candidate?.image || item.product.image;
        if (src) {
          if (item.image.getAttribute('src') !== src) item.image.src = src;
          if (item.hero) this.setHero(item, src);
        }
        item.selectionText.textContent =
          item.selected
            .filter((v) => v != null && v !== 'Default Title')
            .map((v) => this.label(v))
            .join(' · ') || this.t('default_variant');
        if (item.sharedLine) {
          item.sharedLine.hidden = !this.shared;
          const colourName = item.product.options[item.colourIndex];
          item.sharedLineText.textContent = `${this.optionTitle(item, colourName, item.colourIndex)}: ${this.label(this.sharedValue)}`;
        }
      });

      if (this.shared && this.sharedInputs) {
        const blocked = [];
        this.sharedInputs.forEach((input) => {
          const blockers = this.items.filter((item) => !this.availableFor(item, item.colourIndex, input.value));
          input.disabled = blockers.length > 0;
          input.parentElement.classList.toggle('jq-chip--sold-out', input.disabled);
          input.setAttribute('aria-label', `${this.label(input.value)}${input.disabled ? ` — ${this.t('sold_out')}` : ''}`);
          if (input.disabled) blocked.push({ value: input.value, product: blockers[0].product.title });
        });
        if (this.sharedNote) {
          const first = blocked[0];
          const show = first && this.t('shared_unavailable');
          this.sharedNote.hidden = !show;
          if (show) {
            this.sharedNote.textContent = this.t('shared_unavailable')
              .replace(/\[colou?r\]/g, this.label(first.value))
              .replace('[product]', first.product);
          }
        }
      }

      this.refreshPrice();
      let statusText;
      if (this.steps) {
        const last = this.stepIndex === this.steps.length - 1;
        const current = this.items[this.stepIndex];
        this.backButton.hidden = this.stepIndex === 0;
        this.nextButton.hidden = last;
        this.addButton.hidden = !last;
        const nextBlocked = this.busy || current.incomplete || current.unavailable;
        this.nextButton.setAttribute('aria-disabled', String(nextBlocked));
        if (current.incomplete) statusText = this.t('choose_sizes');
        else if (current.unavailable) statusText = this.t('unavailable');
        else if (last) statusText = missing ? this.t('choose_sizes') : unavailable ? this.t('unavailable') : this.t('ready');
        else {
          const label = this.stepLabel(this.stepIndex);
          statusText = this.t('step').includes('[product]') ? label : `${label} · ${current.product.title}`;
        }
      } else {
        this.backButton.hidden = true;
        this.nextButton.hidden = true;
        this.addButton.hidden = false;
        statusText = missing ? this.t('choose_sizes') : unavailable ? this.t('unavailable') : this.t('ready');
      }
      setText(this.status, statusText);
      this.addButton.disabled = this.busy || this.addedWithoutConfirmation || missing || unavailable;
      const addLabel = this.busy ? this.t('adding') : this.t('add');
      setText(this.addButton, this.nextSetIsExtra() ? addLabel : `${addLabel} · ${this.priceText}`);
    }

    /**
     * In percentage mode the set price follows the chosen variants (a larger
     * size or another colour may cost more); a fixed set price stays as entered.
     */
    refreshPrice() {
      if (!this.active || this.active.fixedPrice) return;
      const original = this.items.reduce((sum, item) => {
        const v = item.variant || item.product.variants.find((x) => x.id === item.product.defaultVariant) || item.product.variants[0];
        return sum + (v?.price || 0);
      }, 0);
      const price = Math.round((original * (100 - (this.active.discountPercent || 0))) / 100);
      this.priceCents = price;
      this.priceText = this.format(price);
      setText(this.querySelector('[data-jq-dialog-price]'), this.priceText);
    }

    /* ---------------------------------------------------------------- */
    /* Add to cart                                                       */
    /* ---------------------------------------------------------------- */

    flashCard(card) {
      const button = card?.querySelector('[data-jq-open]');
      if (!button || !this.t('added')) return;
      const original = button.textContent;
      button.textContent = this.t('added');
      button.classList.add('jq-set-button--done');
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => {
        button.textContent = original;
        button.classList.remove('jq-set-button--done');
      }, 3000);
    }

    async add() {
      if (this.busy || this.addButton.disabled || this.items.some((item) => !item.variant?.available)) return;
      this.busy = true;
      this.error.hidden = true;
      this.update();

      const variants = this.items.map((item) => item.variant.id);
      const properties = {
        _bundle_id: `${this.active.key}:${variants.join('-')}`,
        _bundle_title: this.active.title,
        _bundle_size: String(this.items.length),
        // The set price as sold, so the cart can show it exactly: Shopify rounds each
        // line's share of the discount separately (19.900 KD becomes 6.63 + 6.63 + 6.63).
        _bundle_price: String(this.priceCents || 0),
        _bundle_currency: this.currency,
      };
      if (this.offerAmount) properties._bundle_offer = String(this.offerAmount);

      const cartElement = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sectionIds = cartElement?.getSectionsToRender?.().map((section) => section.id) || ['cart-icon-bubble'];
      const essentialPortal = () => document.querySelector('#essential-cart-drawer-portal');
      let added = false;

      try {
        cartElement?.setActiveElement?.(this.trigger);
        const response = await fetch(`${this.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: variants.map((id) => ({ id, quantity: 1, properties })),
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.status) {
          const failure = new Error(result.description || this.t('error'));
          failure.shopper = true;
          throw failure;
        }
        added = true;

        const cartResponse = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!cartResponse.ok) throw new Error(this.t('check_cart'));
        const cart = await cartResponse.json();

        this.memory.delete(this.active.key);
        if (essentialPortal()) this.trigger = null; // the cart drawer takes focus next
        this.dialog.close();

        if (cartElement?.renderContents && !essentialPortal()) {
          const first = result.items?.[0] || {};
          cartElement.renderContents({ ...first, sections: cart.sections || result.sections });
        }
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'jewelique-bundles', cartData: cart });
        }
        document.dispatchEvent(new CustomEvent('jewelique:cart', { detail: cart }));
        if (this.feedback) this.feedback.textContent = this.t('added');
        this.flashCard(this.active.card);

        const portal = essentialPortal();
        if (portal) {
          const dialogVisible = () => {
            const essential = portal.querySelector('[role="dialog"]');
            return essential && getComputedStyle(essential).visibility !== 'hidden' && getComputedStyle(essential).display !== 'none';
          };
          if (!dialogVisible()) document.querySelector('#cart-icon-bubble')?.click();
          setTimeout(() => {
            if (!dialogVisible() && !document.querySelector('cart-drawer.active')) window.location.assign(this.cartUrl);
          }, 2500);
        } else if (!cartElement?.renderContents) {
          window.location.assign(this.cartUrl);
        }
      } catch (error) {
        this.error.textContent = added ? this.t('check_cart') : (error.shopper && error.message) || this.t('error');
        this.error.hidden = false;
        if (added) {
          const link = make('a', 'jq-check-cart', this.t('view_cart'));
          link.href = this.cartUrl;
          this.error.append(' ', link);
          this.addButton.disabled = true;
          this.addButton.textContent = this.t('added');
          this.addedWithoutConfirmation = true;
        }
      } finally {
        this.busy = false;
        if (!this.addedWithoutConfirmation) this.update();
        if (this.pendingRelease) this.release();
      }
    }
  }

  customElements.define('jewelique-bundles', JeweliqueBundles);
})();
